import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Avatar,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  AppBar,
  Toolbar,
  IconButton,
  useMediaQuery,
  useTheme as useMuiTheme,
  styled,
  Tooltip,
} from '@mui/material';
import {
  School as SchoolIcon,
  Forum as ForumIcon,
  ExitToApp as ExitToAppIcon,
  Home as HomeIcon,
  EmojiEvents as LeaderboardIcon,
  Quiz as QuizIcon,
  Dashboard as DashboardIcon,
  AutoAwesome as LogoIcon,
  Menu as MenuIcon,
  Bookmark as BookmarkIcon,
  AdminPanelSettings as AdminPanelSettingsIcon,
  Route as RouteIcon,
  MenuBook as MenuBookIcon,
  Assignment as AssignmentIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  WbSunny as WbSunnyIcon,
  DarkMode as DarkModeIcon,
  Close as CloseIcon,
  ViewModule as ViewModuleIcon,
  RateReview as RateReviewIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import { useOnboarding } from '../context/OnboardingContext';
import Notifications from './UIKit/Notifications';
import ClockWidgetMount from './ClockWidgetMount';
import { getUserAvatarSrc } from '../utils/avatarUtils';

// Styled components for sidebar toggle - always dark like sidebar
const ExternalToggleButton = styled('button')(({ theme, open, drawerWidth, miniDrawerWidth }) => ({
  position: 'fixed',
  top: 10,
  left: open ? drawerWidth - 10 : miniDrawerWidth - 10,
  right: 'auto',
  width: 32,
  height: 32,
  backgroundColor: '#1f2937',
  border: '1px solid #374151',
  borderRadius: 8,
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  paddingRight: 6,
  zIndex: 30,
  boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
  transition: theme.transitions.create(['background-color', 'transform', 'box-shadow', 'border-color', 'left'], {
    duration: 300,
    easing: theme.transitions.easing.easeInOut,
  }),
  '&:hover': {
    backgroundColor: '#374151',
    transform: 'scale(1.05)',
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
  },
  '&:active': {
    transform: 'scale(0.95)',
    boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
  },
  '& svg': {
    fontSize: 18,
    color: '#d1d1d1',
    transition: theme.transitions.create(['transform'], {
      duration: 200,
      easing: theme.transitions.easing.easeInOut,
    }),
  },
  '&:focus': {
    outline: 'none',
  },
}));

// Mixins for drawer states
const openedMixin = (theme, drawerWidth) => ({
  width: drawerWidth,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.easeInOut,
    duration: 300,
  }),
  overflowX: 'hidden',
});

const closedMixin = (theme, miniDrawerWidth) => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.easeInOut,
    duration: 300,
  }),
  overflowX: 'hidden',
  width: miniDrawerWidth,
});

const StyledDrawer = styled(Drawer, {
  shouldForwardProp: (prop) =>
    prop !== 'open' && prop !== 'drawerWidth' && prop !== 'miniDrawerWidth' && prop !== 'sidebarBackgroundColor' && prop !== 'textColor',
})(({ theme, open, drawerWidth, miniDrawerWidth, sidebarBackgroundColor, textColor }) => ({
  width: open ? drawerWidth : miniDrawerWidth,
  flexShrink: 0,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
  '& .MuiDrawer-paper': {
    backgroundColor: sidebarBackgroundColor,
    color: textColor,
    borderRight: '1px solid rgba(255, 255, 255, 0.06)',
  },
  ...(open && {
    ...openedMixin(theme, drawerWidth),
    '& .MuiDrawer-paper': {
      ...openedMixin(theme, drawerWidth),
      backgroundColor: sidebarBackgroundColor,
      color: textColor,
      borderRight: '1px solid rgba(255, 255, 255, 0.06)',
    },
  }),
  ...(!open && {
    ...closedMixin(theme, miniDrawerWidth),
    '& .MuiDrawer-paper': {
      ...closedMixin(theme, miniDrawerWidth),
      backgroundColor: sidebarBackgroundColor,
      color: textColor,
      borderRight: '1px solid rgba(255, 255, 255, 0.06)',
    },
  }),
}));

const Layout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, user, logout } = useAuth();
  const { currentLanguage } = useLanguage();
  const { themeType, toggleTheme } = useTheme();
  const { shouldOpenDrawer, setLayoutRef, isOnboardingActive } =
    useOnboarding();
  const muiTheme = useMuiTheme();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [desktopOpen, setDesktopOpen] = useState(true); // New state for desktop sidebar
  const [forceUpdate, setForceUpdate] = useState(0);
  const [showMore, setShowMore] = useState(false);
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));
  const isDesktop = useMediaQuery(muiTheme.breakpoints.up('lg'));
  
  // Sidebar dimensions
  const drawerWidth = 280;
  const miniDrawerWidth = 73;
  const layoutRef = useRef({
    openDrawer: () => setMobileOpen(true),
    closeDrawer: () => setMobileOpen(false),
    isDrawerOpen: () => mobileOpen,
  });

  const { t } = useTranslation('common');

  // Обновляем методы при изменении mobileOpen
  useEffect(() => {
    layoutRef.current = {
      openDrawer: () => setMobileOpen(true),
      closeDrawer: () => setMobileOpen(false),
      isDrawerOpen: () => mobileOpen,
    };
  }, [mobileOpen]);

  // Передаем ссылку на методы управления дровером в контекст онбординга
  useEffect(() => {
    if (setLayoutRef) {
      setLayoutRef(layoutRef.current);
    }
  }, [setLayoutRef, layoutRef]);

  // Открываем или закрываем дровер при наличии шага онбординга, требующего этого
  useEffect(() => {
    if (shouldOpenDrawer && isMobile) {
      // Для онбординга всегда открываем дровер, если требуется
      console.log('Layout: принудительно открываем дровер для онбординга');
      setMobileOpen(true);
    }
  }, [shouldOpenDrawer, isMobile, mobileOpen, isOnboardingActive]);

  // Обновляем компонент при изменении языка
  useEffect(() => {
    setForceUpdate((prev) => prev + 1);
  }, [currentLanguage]);

  const handleDrawerToggle = () => {
    // Если онбординг активен и требует открытого дровера, то блокируем закрытие
    if (isOnboardingActive && shouldOpenDrawer) {
      setMobileOpen(true);
    } else {
      setMobileOpen(!mobileOpen);
    }
  };

  const handleDesktopToggle = () => {
    setDesktopOpen(!desktopOpen);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Обработка переключения темы
  const handleThemeToggle = () => {
    const newTheme = themeType === 'light' ? 'dark' : 'light';
    toggleTheme(newTheme);
  };

  // Adjust colors to match the image - sidebar is always dark
  const sidebarBackgroundColor = '#1f2937';
  const textColor = '#d1d1d1';
  const hoverBackgroundColor = 'rgba(255, 255, 255, 0.1)';
  const activeBackgroundColor = 'rgba(255, 255, 255, 0.2)';

  // Реализация компонента drawer, который будет перерисовываться при изменении языка
  const renderDrawer = (isCollapsed = false) => (
    <div
      key={forceUpdate}
      style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
    >
      {/* Fixed header */}
      <Box sx={{
        px: 2,
        py: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: isCollapsed ? 'center' : 'space-between',
        backgroundColor: sidebarBackgroundColor,
        borderBottom: '1px solid rgba(255, 255, 255, 0.12)',
        zIndex: 2,
        minHeight: '48px'
      }}>
        <Typography
          variant="h6"
          sx={{
            color: textColor,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            fontWeight: 'bold',
            justifyContent: isCollapsed ? 'center' : 'flex-start',
          }}
        >
          <LogoIcon sx={{ width: 24, height: 24 }} />
          {!isCollapsed && 'Game Academy'}
        </Typography>
        {!isCollapsed && !isDesktop && (
          <IconButton
            onClick={handleDrawerToggle}
            sx={{
              color: textColor,
              '&:hover': {
                backgroundColor: hoverBackgroundColor,
              },
            }}
            aria-label="close drawer"
          >
            <CloseIcon />
          </IconButton>
        )}
      </Box>
      {/* Scrollable content container */}
      <Box sx={{
        flex: 1,
        overflowY: 'auto',
        overflowX: 'hidden',
      }}>
        <List
          className="sidebar-menu"
          sx={{
            mx: '8px',
            '& .MuiListItemIcon-root': { minWidth: '36px' },
          }}
        >
        <Tooltip title={t('common:navigation.home')} placement="right" arrow disableHoverListener={!isCollapsed}>
          <ListItem
            component={Link}
            to="/"
            onClick={() => setMobileOpen(false)}
            sx={{
              '&:hover': { cursor: 'pointer', backgroundColor: hoverBackgroundColor },
              padding: isCollapsed ? '8px 0px' : '8px 16px',
              bgcolor: location.pathname === '/' ? activeBackgroundColor : 'transparent',
              borderRadius: '8px',
              marginBottom: '8px',
              textDecoration: 'none',
              justifyContent: isCollapsed ? 'center' : 'flex-start',
            }}
          >
            <ListItemIcon sx={{ minWidth: isCollapsed ? 'auto' : '36px', justifyContent: isCollapsed ? 'center' : 'flex-start' }}>
              <HomeIcon sx={{ color: textColor }} />
            </ListItemIcon>
            {!isCollapsed && <ListItemText primary={t('common:navigation.home')} sx={{ color: textColor }} />}
          </ListItem>
        </Tooltip>
        <Tooltip title={t('common:navigation.learningPathsTitle')} placement="right" arrow disableHoverListener={!isCollapsed}>
          <ListItem
            component={Link}
            to="/learning-paths"
            onClick={() => setMobileOpen(false)}
            sx={{
              '&:hover': { cursor: 'pointer', backgroundColor: hoverBackgroundColor },
              padding: isCollapsed ? '8px 0px' : '8px 16px',
              bgcolor: location.pathname === '/learning-paths' ? activeBackgroundColor : 'transparent',
              borderRadius: '8px',
              marginBottom: '8px',
              textDecoration: 'none',
              justifyContent: isCollapsed ? 'center' : 'flex-start',
            }}
          >
            <ListItemIcon sx={{ minWidth: isCollapsed ? 'auto' : '36px', justifyContent: isCollapsed ? 'center' : 'flex-start' }}>
              <RouteIcon sx={{ color: textColor }} />
            </ListItemIcon>
            {!isCollapsed && <ListItemText primary={t('common:navigation.learningPathsTitle')} sx={{ color: textColor }} />}
          </ListItem>
        </Tooltip>
        <Tooltip title={t('common:navigation.courses')} placement="right" arrow disableHoverListener={!isCollapsed}>
          <ListItem
            component={Link}
            to="/courses"
            onClick={() => setMobileOpen(false)}
            sx={{
              '&:hover': { cursor: 'pointer', backgroundColor: hoverBackgroundColor },
              padding: isCollapsed ? '8px 0px' : '8px 16px',
              bgcolor: location.pathname === '/courses' ? activeBackgroundColor : 'transparent',
              borderRadius: '8px',
              marginBottom: '8px',
              textDecoration: 'none',
              justifyContent: isCollapsed ? 'center' : 'flex-start',
            }}
          >
            <ListItemIcon sx={{ minWidth: isCollapsed ? 'auto' : '36px', justifyContent: isCollapsed ? 'center' : 'flex-start' }}>
              <SchoolIcon sx={{ color: textColor }} />
            </ListItemIcon>
            {!isCollapsed && <ListItemText primary={t('common:navigation.courses')} sx={{ color: textColor }} />}
          </ListItem>
        </Tooltip>
        {/* "More" toggle — only when sidebar is expanded */}
        {!isCollapsed && (
          <>
            <ListItem
              onClick={() => setShowMore((v) => !v)}
              sx={{
                '&:hover': { cursor: 'pointer', backgroundColor: hoverBackgroundColor },
                padding: '8px 16px',
                borderRadius: '8px',
                marginBottom: '8px',
                justifyContent: 'flex-start',
              }}
            >
              <ListItemIcon sx={{ minWidth: '36px' }}>
                {showMore ? <ExpandLessIcon sx={{ color: textColor }} /> : <ExpandMoreIcon sx={{ color: textColor }} />}
              </ListItemIcon>
              <ListItemText
                primary={showMore ? t('common:navigation.less', 'Less') : t('common:navigation.more', 'More')}
                sx={{ color: textColor }}
              />
            </ListItem>
          </>
        )}

        {/* Secondary nav items — hidden until "More" is expanded */}
        {!isCollapsed && showMore && (
          <>
            <Tooltip title={t('common:navigation.forum')} placement="right" arrow disableHoverListener>
              <ListItem
                component={Link}
                to="/forum"
                onClick={() => setMobileOpen(false)}
                sx={{
                  '&:hover': { cursor: 'pointer', backgroundColor: hoverBackgroundColor },
                  padding: '8px 16px',
                  bgcolor: location.pathname === '/forum' ? activeBackgroundColor : 'transparent',
                  borderRadius: '8px',
                  marginBottom: '8px',
                  textDecoration: 'none',
                }}
              >
                <ListItemIcon sx={{ minWidth: '36px' }}>
                  <ForumIcon sx={{ color: textColor }} />
                </ListItemIcon>
                <ListItemText primary={t('common:navigation.forum')} sx={{ color: textColor }} />
              </ListItem>
            </Tooltip>

            {isAuthenticated && (
              <Tooltip title={t('common:navigation.leaderboard')} placement="right" arrow disableHoverListener>
                <ListItem
                  component={Link}
                  to="/leaderboard"
                  onClick={() => setMobileOpen(false)}
                  sx={{
                    '&:hover': { cursor: 'pointer', backgroundColor: hoverBackgroundColor },
                    padding: '8px 16px',
                    bgcolor: location.pathname === '/leaderboard' ? activeBackgroundColor : 'transparent',
                    borderRadius: '8px',
                    marginBottom: '8px',
                    textDecoration: 'none',
                  }}
                >
                  <ListItemIcon sx={{ minWidth: '36px' }}>
                    <LeaderboardIcon sx={{ color: textColor }} />
                  </ListItemIcon>
                  <ListItemText primary={t('common:navigation.leaderboard')} sx={{ color: textColor }} />
                </ListItem>
              </Tooltip>
            )}

            {isAuthenticated && (
              <Tooltip title={t('common:navigation.myTests')} placement="right" arrow disableHoverListener>
                <ListItem
                  component={Link}
                  to="/test-results"
                  onClick={() => setMobileOpen(false)}
                  sx={{
                    '&:hover': { cursor: 'pointer', backgroundColor: hoverBackgroundColor },
                    padding: '8px 16px',
                    bgcolor: location.pathname === '/test-results' ? activeBackgroundColor : 'transparent',
                    borderRadius: '8px',
                    marginBottom: '8px',
                    textDecoration: 'none',
                  }}
                >
                  <ListItemIcon sx={{ minWidth: '36px' }}>
                    <QuizIcon sx={{ color: textColor }} />
                  </ListItemIcon>
                  <ListItemText primary={t('common:navigation.myTests')} sx={{ color: textColor }} />
                </ListItem>
              </Tooltip>
            )}

            {isAuthenticated && (
              <Tooltip title={t('common:navigation.bookmarks')} placement="right" arrow disableHoverListener>
                <ListItem
                  component={Link}
                  to="/bookmarks"
                  onClick={() => setMobileOpen(false)}
                  sx={{
                    '&:hover': { cursor: 'pointer', backgroundColor: hoverBackgroundColor },
                    padding: '8px 16px',
                    bgcolor: location.pathname === '/bookmarks' ? activeBackgroundColor : 'transparent',
                    borderRadius: '8px',
                    marginBottom: '8px',
                    textDecoration: 'none',
                  }}
                >
                  <ListItemIcon sx={{ minWidth: '36px' }}>
                    <BookmarkIcon sx={{ color: textColor }} />
                  </ListItemIcon>
                  <ListItemText primary={t('common:navigation.bookmarks')} sx={{ color: textColor }} />
                </ListItem>
              </Tooltip>
            )}

            {isAuthenticated && (
              <Tooltip title={t('common:navigation.analytics')} placement="right" arrow disableHoverListener>
                <ListItem
                  component={Link}
                  to="/dashboard"
                  onClick={() => setMobileOpen(false)}
                  sx={{
                    '&:hover': { cursor: 'pointer', backgroundColor: hoverBackgroundColor },
                    padding: '8px 16px',
                    bgcolor: location.pathname === '/dashboard' ? activeBackgroundColor : 'transparent',
                    borderRadius: '8px',
                    marginBottom: '8px',
                    textDecoration: 'none',
                  }}
                >
                  <ListItemIcon sx={{ minWidth: '36px' }}>
                    <DashboardIcon sx={{ color: textColor }} />
                  </ListItemIcon>
                  <ListItemText primary={t('common:navigation.analytics')} sx={{ color: textColor }} />
                </ListItem>
              </Tooltip>
            )}

            {isAuthenticated && (
              <Tooltip title={t('common:navigation.lessonsLibraryTitle')} placement="right" arrow disableHoverListener>
                <ListItem
                  component={Link}
                  to="/lessons"
                  onClick={() => setMobileOpen(false)}
                  sx={{
                    '&:hover': { cursor: 'pointer', backgroundColor: hoverBackgroundColor },
                    padding: '8px 16px',
                    bgcolor: location.pathname === '/lessons' ? activeBackgroundColor : 'transparent',
                    borderRadius: '8px',
                    marginBottom: '8px',
                    textDecoration: 'none',
                  }}
                >
                  <ListItemIcon sx={{ minWidth: '36px' }}>
                    <MenuBookIcon sx={{ color: textColor }} />
                  </ListItemIcon>
                  <ListItemText primary={t('common:navigation.lessonsLibraryTitle')} sx={{ color: textColor }} />
                </ListItem>
              </Tooltip>
            )}

            {isAuthenticated && (
              <Tooltip title={t('common:navigation.modules')} placement="right" arrow disableHoverListener>
                <ListItem
                  component={Link}
                  to="/modules"
                  onClick={() => setMobileOpen(false)}
                  sx={{
                    '&:hover': { cursor: 'pointer', backgroundColor: hoverBackgroundColor },
                    padding: '8px 16px',
                    bgcolor: location.pathname === '/modules' ? activeBackgroundColor : 'transparent',
                    borderRadius: '8px',
                    marginBottom: '8px',
                    textDecoration: 'none',
                  }}
                >
                  <ListItemIcon sx={{ minWidth: '36px' }}>
                    <ViewModuleIcon sx={{ color: textColor }} />
                  </ListItemIcon>
                  <ListItemText primary={t('common:navigation.modules')} sx={{ color: textColor }} />
                </ListItem>
              </Tooltip>
            )}

            {user?.userMeta?.roles && !user.userMeta.roles.includes('admin') && (
              <Tooltip title={t('common:navigation.myChangeRequests')} placement="right" arrow disableHoverListener>
                <ListItem
                  component={Link}
                  to="/my-change-requests"
                  onClick={() => setMobileOpen(false)}
                  sx={{
                    '&:hover': { cursor: 'pointer', backgroundColor: hoverBackgroundColor },
                    padding: '8px 16px',
                    bgcolor: location.pathname === '/my-change-requests' ? activeBackgroundColor : 'transparent',
                    borderRadius: '8px',
                    marginBottom: '8px',
                    textDecoration: 'none',
                  }}
                >
                  <ListItemIcon sx={{ minWidth: '36px' }}>
                    <AssignmentIcon sx={{ color: textColor }} />
                  </ListItemIcon>
                  <ListItemText primary={t('common:navigation.myChangeRequests')} sx={{ color: textColor }} />
                </ListItem>
              </Tooltip>
            )}
          </>
        )}

        {/* Admin items — always visible for admins/teachers */}
        {user?.userMeta?.roles?.includes('admin') && (
          <Tooltip title={t('common:navigation.adminTitle')} placement="right" arrow disableHoverListener={!isCollapsed}>
            <ListItem
              component={Link}
              to="/admin"
              onClick={() => setMobileOpen(false)}
              sx={{
                '&:hover': { cursor: 'pointer', backgroundColor: hoverBackgroundColor },
                padding: isCollapsed ? '8px 0px' : '8px 16px',
                bgcolor: location.pathname === '/admin' ? activeBackgroundColor : 'transparent',
                borderRadius: '8px',
                marginBottom: '8px',
                textDecoration: 'none',
                justifyContent: isCollapsed ? 'center' : 'flex-start',
              }}
            >
              <ListItemIcon sx={{ minWidth: isCollapsed ? 'auto' : '36px', justifyContent: isCollapsed ? 'center' : 'flex-start' }}>
                <AdminPanelSettingsIcon sx={{ color: textColor }} />
              </ListItemIcon>
              {!isCollapsed && <ListItemText primary={t('common:navigation.adminTitle')} sx={{ color: textColor }} />}
            </ListItem>
          </Tooltip>
        )}
        {user?.userMeta?.roles && (user.userMeta.roles.includes('admin') || user.userMeta.roles.includes('teacher')) && (
          <Tooltip title={t('common:navigation.openAnswerReview')} placement="right" arrow disableHoverListener={!isCollapsed}>
            <ListItem
              component={Link}
              to="/admin/open-answer-review"
              onClick={() => setMobileOpen(false)}
              sx={{
                '&:hover': { cursor: 'pointer', backgroundColor: hoverBackgroundColor },
                padding: isCollapsed ? '8px 0px' : '8px 16px',
                bgcolor: location.pathname === '/admin/open-answer-review' ? activeBackgroundColor : 'transparent',
                borderRadius: '8px',
                marginBottom: '8px',
                textDecoration: 'none',
                justifyContent: isCollapsed ? 'center' : 'flex-start',
              }}
            >
              <ListItemIcon sx={{ minWidth: isCollapsed ? 'auto' : '36px', justifyContent: isCollapsed ? 'center' : 'flex-start' }}>
                <RateReviewIcon sx={{ color: textColor }} />
              </ListItemIcon>
              {!isCollapsed && <ListItemText primary={t('common:navigation.openAnswerReview')} sx={{ color: textColor }} />}
            </ListItem>
          </Tooltip>
        )}
        </List>
      </Box>

      {/* Fixed bottom section */}
      <Box sx={{
        backgroundColor: sidebarBackgroundColor,
        borderTop: '1px solid rgba(255, 255, 255, 0.12)',
        zIndex: 2
      }}>
        <Tooltip
          title={themeType === 'dark' ? 'Light Mode' : 'Dark Mode'}
          placement="right"
          arrow
          disableHoverListener={!isCollapsed}
        >
          <Box
            sx={{
              '&:hover': { cursor: 'pointer', backgroundColor: hoverBackgroundColor },
              padding: isCollapsed ? '8px 0px' : '8px 16px',
              borderRadius: '8px',
              marginBottom: 0,
              marginX: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: isCollapsed ? 'center' : 'flex-start',
              minHeight: '48px',
            }}
            onClick={handleThemeToggle}
          >
            <ListItemIcon sx={{ minWidth: isCollapsed ? 'auto' : '36px', justifyContent: isCollapsed ? 'center' : 'flex-start' }}>
              {themeType === 'dark' ? (
                <WbSunnyIcon sx={{ color: textColor }} />
              ) : (
                <DarkModeIcon sx={{ color: textColor }} />
              )}
            </ListItemIcon>
            {!isCollapsed && (
              <ListItemText
                primary={themeType === 'dark' ? 'Light Mode' : 'Dark Mode'}
                sx={{ color: textColor }}
              />
            )}
          </Box>
        </Tooltip>
        {isAuthenticated ? (
          <Box sx={{ mb: 1 }}>
            {/* Profile and Logout Items */}
            {/* Profile section - only when sidebar is collapsed */}
            {isDesktop && !desktopOpen ? (
              <Tooltip
                title={
                  <Box sx={{ p: 0.5 }}>
                    <Typography variant="body2" fontWeight="bold">
                      {user?.userMeta?.fullName || user?.userMeta?.email || 'User'}
                    </Typography>
                    {user?.userMeta?.email && (
                      <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                        {user?.userMeta?.email}
                      </Typography>
                    )}
                    <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                      Role: {user?.userMeta?.roles && user?.userMeta?.roles.length > 0
                        ? user?.userMeta?.roles.join(', ')
                        : t('common:navigation.defaultRole')}
                    </Typography>
                  </Box>
                }
                placement="right"
                arrow
                disableHoverListener={false}
              >
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    '&:hover': {
                      cursor: 'pointer',
                      backgroundColor: hoverBackgroundColor,
                    },
                    borderRadius: '8px',
                    padding: '8px',
                    width: 'calc(100% - 16px)',
                    marginX: '8px',
                  }}
                >
                  <Avatar
                    alt={
                      user?.userMeta?.fullName || user?.userMeta?.email || 'User'
                    }
                    src={getUserAvatarSrc(user)}
                    sx={{ width: 36, height: 36, flexShrink: 0 }}
                  />
                </Box>
              </Tooltip>
            ) : (
              /* Profile section - when sidebar is expanded */
              <Box
                sx={{
                  padding: isCollapsed ? '8px 0px' : '8px 16px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  '&:hover': {
                    cursor: 'pointer',
                    backgroundColor: hoverBackgroundColor,
                  },
                  borderRadius: '8px',
                  marginX: '8px',
                  justifyContent: isCollapsed ? 'center' : 'flex-start',
                }}
              >
                <Tooltip
                  title={
                    <Box sx={{ p: 0.5 }}>
                      <Typography variant="body2" fontWeight="bold">
                        {user?.userMeta?.fullName || user?.userMeta?.email || 'User'}
                      </Typography>
                      {user?.userMeta?.email && (
                        <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                          {user?.userMeta?.email}
                        </Typography>
                      )}
                      <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                        Role: {user?.userMeta?.roles && user?.userMeta?.roles.length > 0
                          ? user?.userMeta?.roles.join(', ')
                          : t('common:navigation.defaultRole')}
                      </Typography>
                    </Box>
                  }
                  placement="right"
                  arrow
                  disableHoverListener={false}
                >
                  <Box
                    component={Link}
                    to="/profile"
                    className="profile"
                    onClick={() => setMobileOpen(false)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: isCollapsed ? 0 : 1.5,
                      flex: 1,
                      textDecoration: 'none',
                      justifyContent: isCollapsed ? 'center' : 'flex-start',
                      minWidth: 0,
                    }}
                  >
                    <Avatar
                      alt={
                        user?.userMeta?.fullName || user?.userMeta?.email || 'User'
                      }
                      src={getUserAvatarSrc(user)}
                      sx={{ width: 36, height: 36, flexShrink: 0 }}
                    />
                    {!isCollapsed && (
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          variant="subtitle1"
                          sx={{
                            color: textColor,
                            lineHeight: 1.2,
                            fontSize: '0.875rem',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {user?.userMeta?.fullName ||
                            user?.userMeta?.email ||
                            t('common:navigation.defaultUsername')}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: 'rgba(255, 255, 255, 0.7)',
                            fontSize: '0.75rem',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          Role: {user?.userMeta?.roles && user?.userMeta?.roles.length > 0
                            ? user?.userMeta?.roles.join(', ')
                            : t('common:navigation.defaultRole')}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Tooltip>

                {/* Desktop notifications in sidebar - right of profile */}
                {isDesktop && desktopOpen && (
                  <Box sx={{ ml: 1 }} onClick={(e) => e.stopPropagation()}>
                    <Notifications disableTooltip={true} />
                  </Box>
                )}
              </Box>
            )}

            {/* Desktop notifications below avatar when sidebar is collapsed */}
            {isDesktop && !desktopOpen && (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  '&:hover': {
                    cursor: 'pointer',
                    backgroundColor: hoverBackgroundColor,
                  },
                  borderRadius: '8px',
                  padding: '8px',
                  width: 'calc(100% - 16px)',
                  marginX: '8px',
                }}
              >
                <Notifications />
              </Box>
            )}

            <Tooltip
              title={t('common:navigation.logout')}
              placement="right"
              arrow
              disableHoverListener={!isCollapsed}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  '&:hover': {
                    cursor: 'pointer',
                    backgroundColor: hoverBackgroundColor,
                  },
                  borderRadius: '8px',
                  marginBottom: '8px',
                  marginX: '8px',
                  padding: isCollapsed ? '8px 0px' : '8px 16px',
                  justifyContent: isCollapsed ? 'center' : 'flex-start',
                  '& .MuiListItemIcon-root': { minWidth: isCollapsed ? 'auto' : '36px', justifyContent: isCollapsed ? 'center' : 'flex-start' },
                }}
                onClick={handleLogout}
              >
                <ListItemIcon>
                  <ExitToAppIcon sx={{ color: textColor }} />
                </ListItemIcon>
                {!isCollapsed && (
                  <ListItemText primary={t('common:navigation.logout')} sx={{ color: textColor }} />
                )}
              </Box>
            </Tooltip>
          </Box>
        ) : (
          <List
            sx={{
              mx: '8px',
              p: 0,
              '& .MuiListItemIcon-root': { minWidth: isCollapsed ? 'auto' : '36px', justifyContent: isCollapsed ? 'center' : 'flex-start' },
            }}
          >
            {/* Login and Register Items */}
            <Tooltip
              title={t('common:navigation.login')}
              placement="right"
              arrow
              disableHoverListener={!isCollapsed}
            >
            <ListItem
              component="div"
              onClick={() => {
                navigate('/login');
                setMobileOpen(false);
              }}
              sx={{
                '&:hover': {
                  cursor: 'pointer',
                  backgroundColor: hoverBackgroundColor,
                },
                padding: isCollapsed ? '8px 0px' : '8px 16px',
                borderRadius: '8px',
                marginBottom: 0,
                justifyContent: isCollapsed ? 'center' : 'flex-start',
              }}
            >
                <ListItemIcon>
                  <ExitToAppIcon sx={{ color: textColor }} />
                </ListItemIcon>
                {!isCollapsed && (
                  <ListItemText primary={t('common:navigation.login')} sx={{ color: textColor }} />
                )}
              </ListItem>
            </Tooltip>
            <Tooltip
              title={t('common:navigation.register')}
              placement="right"
              arrow
              disableHoverListener={!isCollapsed}
            >
            <ListItem
              component="div"
              onClick={() => {
                navigate('/register');
                setMobileOpen(false);
              }}
              sx={{
                '&:hover': {
                  cursor: 'pointer',
                  backgroundColor: hoverBackgroundColor,
                },
                padding: isCollapsed ? '8px 0px' : '8px 16px',
                borderRadius: '8px',
                marginBottom: 0,
                justifyContent: isCollapsed ? 'center' : 'flex-start',
              }}
            >
                <ListItemIcon>
                  <ExitToAppIcon sx={{ color: textColor }} />
                </ListItemIcon>
                {!isCollapsed && (
                  <ListItemText primary={t('common:navigation.register')} sx={{ color: textColor }} />
                )}
              </ListItem>
            </Tooltip>
          </List>
        )}
      </Box>
    </div>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar position="fixed" sx={{ display: { lg: 'none' } }}>
        <Toolbar sx={{ px: { xs: 2, sm: 2, md: 2, lg: 2, xl: 2 }, py: { xs: 1, sm: 1, md: 1, lg: 1, xl: 1 } }}>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2 }}
            data-testid="drawer-toggle"
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            Game Academy
          </Typography>
          {isAuthenticated && !isDesktop && (
            <Box className="notifications">
              <Notifications />
            </Box>
          )}
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { lg: desktopOpen ? drawerWidth : miniDrawerWidth }, flexShrink: { lg: 0 } }}
        className="main-navigation"
      >
        {/* Mobile/Tablet Drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            display: { xs: 'block', lg: 'none' },
            zIndex: 9999,
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: { xs: '100%', sm: '100%', md: '100%' },
              zIndex: 9999
            },
          }}
          data-drawer-type="mobile"
        >
          <Box
            sx={{
              backgroundColor: sidebarBackgroundColor,
              height: '100%',
            }}
            data-drawer-content="mobile"
          >
            {renderDrawer(false)}
          </Box>
        </Drawer>
        
        {/* Desktop Drawer */}
        {isDesktop ? (
          <Box sx={{ position: 'relative' }}>
            <StyledDrawer
              variant="permanent"
              open={desktopOpen}
              drawerWidth={drawerWidth}
              miniDrawerWidth={miniDrawerWidth}
              sidebarBackgroundColor={sidebarBackgroundColor}
              textColor={textColor}
              data-drawer-type="desktop"
            >
              <Box
                sx={{
                  backgroundColor: sidebarBackgroundColor,
                  height: '100%',
                }}
                data-drawer-content="desktop"
              >
                {renderDrawer(!desktopOpen)}
              </Box>
            </StyledDrawer>

            {/* External toggle button for desktop */}
            <ExternalToggleButton
              open={desktopOpen}
              drawerWidth={drawerWidth}
              miniDrawerWidth={miniDrawerWidth}
              onClick={handleDesktopToggle}
              aria-label={desktopOpen ? 'Collapse sidebar' : 'Expand sidebar'}
            >
              {desktopOpen ? <ChevronLeftIcon /> : <ChevronRightIcon />}
            </ExternalToggleButton>
          </Box>
        ) : null}
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 2,
          pt: { xs: 5, sm: 5, lg: 3 },
          width: {
            xs: '100%',
            sm: isDesktop
              ? `calc(100% - ${desktopOpen ? drawerWidth : miniDrawerWidth}px)`
              : '100%'
          },
          minHeight: '100dvh',
        }}
      >
        {children}
      </Box>
      {isAuthenticated && <ClockWidgetMount />}
    </Box>
  );
};

export default Layout;
